import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Switch,
  Text,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  useBreakpointValue,
  extendTheme,
  ChakraProvider,
  Image,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  auth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";

import signInImage from "../img/SignInImg.png";
import smartLabLogo from "../img/SmartLabLogo.png";

const theme = extendTheme({
  colors: {
    customTeal: {
      500: "#4fd1c5",
      600: "#3cb7ac",
      700: "#2e9087",
    },
  },
  components: {
    Switch: {
      baseStyle: {
        track: {
          _checked: {
            bg: "#120f73",
          },
          _focus: {
            boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.3)",
          },
          _focusVisible: {
            boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.3)",
          },
        },
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            borderColor: "gray.300",
            _hover: {
              borderColor: "",
              bg: "gray.50",
            },
            _focus: {
              borderColor: "",
              boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.1)",
              bg: "gray.50",
            },
            _focusVisible: {
              borderColor: "",
              boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.1)",
            },
          },
        },
      },
    },
    Button: {
      baseStyle: {
        _focus: {
          boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.3)",
        },
        _focusVisible: {
          boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.3)",
        },
      },
    },
  },
});

function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail || !password) {
      setError("We couldn't verify your login details. Please try again.");
      setLoading(false);
      return;
    }

    if (!trimmedEmail.includes("@")) {
      setError("We couldn't verify your login details. Please try again.");
      setLoading(false);
      return;
    }

    try {
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      await signInWithEmailAndPassword(auth, trimmedEmail, password);

      if (rememberMe) {
        localStorage.setItem("rememberedEmail", trimmedEmail);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      navigate("/dashboard");
    } catch (err) {
      setError("We couldn't verify your login details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const titleColor = useColorModeValue("#120f73", "#1a1594");
  const textColor = useColorModeValue("gray.400", "whiteAlpha.700");
  const cardWidth = useBreakpointValue({ base: "90%", md: "1100px" });
  void cardWidth;

  return (
    <ChakraProvider theme={theme}>
      <Flex
        justify="center"
        align="center"
        minH="100vh"
        bg="#eff3fb"
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          w="1200px"
          h="800px"
          borderRadius="50px"
          bg="radial-gradient(circle at 100% 40%, #e7ecf7 0%, #d3d9e9 70%, #d8d5f0 100%)"
          filter="blur(45px)"
          zIndex={0}
        />

        <Box
          position="absolute"
          w="900px"
          h="610px"
          bgGradient="linear(to-br, #120f73, #1a1594)"
          borderRadius="25px"
          transform="rotate(7deg)"
          boxShadow="2xl"
        />

        <Flex
          position="relative"
          w={{ base: "auto", md: "1000px" }}
          h={{ base: "auto", md: "630px" }}
          style={{ userSelect: "none" }}
          bg="#f4f7fe"
          borderRadius="25px"
          boxShadow="xl"
          overflow="hidden"
          zIndex="2"
        >
          <Flex
            direction="column"
            w={{ base: "100%", md: "45%" }}
            p={{ base: 8, md: 12 }}
            justify="center"
            position="relative"
          >
            {error && (
              <Alert
                status="error"
                position="absolute"
                top="10px"
                left="10px"
                width="340px"
                maxW="340px"
                borderRadius="15px 4px 4px 4px"
                boxShadow="md"
                zIndex="3"
                fontFamily="poppins"
                fontSize="13px"
              >
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Image
              src={smartLabLogo}
              alt="SmartLab Logo"
              w="330px"
              mt={-4}
              mb={1}
              ml={-7}
              alignSelf="flex-start"
              objectFit="contain"
              filter="drop-shadow(0px 5px 12px rgba(0,0,0,0.15))"
              pointerEvents="none"
            />

            <Heading
              fontFamily="space grotesk"
              fontSize="25px"
              color={titleColor}
              mb={1}
            >
              Welcome Back
            </Heading>

            <Text fontFamily="poppins" fontSize="14px" mb={7} color={textColor}>
              Enter room and password to sign in
            </Text>

            <form onSubmit={handleLogin} autoComplete="off">
              <FormControl fontFamily="poppins" fontSize="15px" mb={4}>
                <FormLabel>
                  Email <Text as="span" color="gray.300"> </Text>
                </FormLabel>
                <Input
                  type="text"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  borderRadius="md"
                  fontSize="12px"
                  h="35px"
                  w="100%"
                  pl={3}
                  required
                  variant="outline"
                  autoComplete={rememberMe ? "email" : "off"}
                  _focus={{
                    borderColor: "#1a1594",
                    boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.1)",
                    bg: "gray.50",
                  }}
                />
              </FormControl>

              <FormControl fontFamily="poppins" fontSize="15px" mb={4}>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    borderRadius="md"
                    fontSize="12px"
                    h="35px"
                    w="100%"
                    pl={3}
                    required
                    variant="outline"
                    autoComplete={
                      rememberMe ? "current-password" : "new-password"
                    }
                    _focus={{
                      borderColor: "#1a1594",
                      boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.1)",
                      bg: "gray.50",
                    }}
                  />
                  <InputRightElement h="35px">
                    <IconButton
                      variant="ghost"
                      onClick={() => setShowPassword(!showPassword)}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      color="#120f73"
                      _hover={{ color: "#1a1594", border: "none" }}
                      _focus={{ border: "none", boxShadow: "none" }}
                      _focusVisible={{ border: "none", boxShadow: "none" }}
                      _active={{ border: "none" }}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      size="sm"
                      border="none"
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl
                fontFamily="poppins"
                display="flex"
                alignItems="center"
                mb={7}
              >
                <Switch
                  id="remember-login"
                  colorScheme="customTeal"
                  mr="10px"
                  isChecked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  _focus={{
                    boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.3)",
                  }}
                />
                <FormLabel htmlFor="remember-login" mb="0" fontSize="14px">
                  Remember me
                </FormLabel>
              </FormControl>

              <Button
                type="submit"
                bg="#120f73"
                w="100%"
                borderRadius="md"
                isDisabled={loading}
                color="white"
                _hover={{ bg: "#1a1594" }}
                _active={{ bg: "#1a1594" }}
                fontFamily="poppins"
                _focus={{
                  boxShadow: "0 0 0 3px rgba(79, 209, 197, 0.3)",
                }}
              >
                {loading ? <Spinner size="sm" /> : "SIGN IN"}
              </Button>
            </form>

            <Text
              mt="100px"
              textAlign="center"
              fontFamily="poppins"
              fontSize="11px"
              color={textColor}
            >
              © 2026 Smart Lab | All rights reserved.
            </Text>
          </Flex>

          <Box
            w="55%"
            display={{ base: "none", md: "flex" }}
            bgImage={`url(${signInImage})`}
            bgSize="cover"
            bgPosition="center"
            borderBottomLeftRadius="50px"
            position="relative"
            alignItems="center"
            justifyContent="center"
          />
        </Flex>
      </Flex>
    </ChakraProvider>
  );
}

export default SignIn;